import mapboxgl from 'mapbox-gl';

export const setupMapEventHandlers = (
  map: mapboxgl.Map,
  zoneFeatureMapRef: React.MutableRefObject<Map<string, number[]>>,
  selectedZones: Set<string>,
  setSelectedZones: React.Dispatch<React.SetStateAction<Set<string>>>,
  handleZoneSelect: (zoneId: string) => void,
) => {
  let hoveredFeatureId: number | null = null;

  // Helper function to update hover state
  const updateHoverState = (e: mapboxgl.MapMouseEvent) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const featureId = feature.id;

      if (featureId !== undefined && featureId !== hoveredFeatureId) {
        // Remove hover state from previously hovered feature
        if (hoveredFeatureId !== null) {
          try {
            map.setFeatureState({ source: 'tso-zones', id: hoveredFeatureId }, { hovered: false });
          } catch {
            // Feature might not exist anymore, ignore
          }
        }

        // Set hover state on current feature
        hoveredFeatureId = featureId as number;
        try {
          map.setFeatureState({ source: 'tso-zones', id: hoveredFeatureId }, { hovered: true });
        } catch {
          // Feature might not exist, ignore
        }
      }
    }
  };

  // Set cursor and initial hover state on mouseenter
  map.on('mouseenter', 'tso-zones-fill', (e) => {
    map.getCanvas().style.cursor = 'pointer';
    updateHoverState(e);
  });

  // Track hover state changes as mouse moves between zones
  map.on('mousemove', 'tso-zones-fill', (e) => {
    updateHoverState(e);
  });

  // Clear hover state and cursor on mouseleave
  map.on('mouseleave', 'tso-zones-fill', () => {
    map.getCanvas().style.cursor = '';

    // Remove hover state from any hovered feature
    if (hoveredFeatureId !== null) {
      try {
        map.setFeatureState({ source: 'tso-zones', id: hoveredFeatureId }, { hovered: false });
      } catch {
        // Feature might not exist anymore, ignore
      }
      hoveredFeatureId = null;
    }
  });

  // Add click handler for zone selection
  map.on('click', 'tso-zones-fill', (e) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const properties = feature.properties;
      const marketZoneId = properties?.market_zone_id;
      const featureId = feature.id;

      if (marketZoneId && featureId !== undefined) {
        handleZoneSelect(marketZoneId);
      }
    }
  });
};

export const clearAllSelections = (
  map: mapboxgl.Map,
  zoneFeatureMapRef: React.MutableRefObject<Map<string, number[]>>,
  selectedZones: Set<string>,
  setSelectedZones: React.Dispatch<React.SetStateAction<Set<string>>>,
) => {
  // Clear all feature states using the zone-feature mapping
  selectedZones.forEach((marketZoneId) => {
    const featureIds = zoneFeatureMapRef.current.get(marketZoneId);
    if (featureIds) {
      featureIds.forEach((featureId: number) => {
        map.setFeatureState({ source: 'tso-zones', id: featureId }, { selected: false });
      });
    }
  });

  setSelectedZones(new Set());
};
